/**
 * Player-facing render settings.
 *
 * These are the switches that can be flipped **while the game is running**.
 * `game/compat.ts` has a fourth (`specular`), which is deliberately not here: it
 * changes material defines, so applying it needs every material rebuilt, and a
 * setting that silently requires a reload is worse than one that does not exist.
 * It stays a URL flag for debugging.
 *
 * The values live in the store and are applied to the scene by `main.tsx`, so
 * Solid never touches a Babylon class and Babylon never reads a Solid signal —
 * the same boundary everything else respects.
 */

export interface Settings {
  /** The pixel-art post-process. */
  pixelate: boolean;
  /** The colour grade and vignette (`game/grading.ts`). */
  grading: boolean;
  /** Exponential distance fog. */
  fog: boolean;
  /** Shadow casting. The cheapest thing to turn off on a weak GPU. */
  shadows: boolean;
  /**
   * Master output volume, 0 to 1 — everything the game plays.
   *
   * Zero is a real value and means silence; there is no separate mute, because
   * two controls for one outcome is one too many.
   */
  volume: number;
  /**
   * Music volume, 0 to 1, scaling the music bus **under** the master.
   *
   * Separate because music is the thing players turn down first: it plays
   * continuously while everything else is a response to something they did, and
   * wanting a quieter score is not the same as wanting a quieter game.
   */
  musicVolume: number;
}

/** The settings that are on/off. */
export type ToggleKey = 'pixelate' | 'grading' | 'fog' | 'shadows';

/** The settings that are a continuous 0..1. */
export type SliderKey = 'volume' | 'musicVolume';

/** One toggle's identity and how to describe it. */
export interface SettingDef {
  key: ToggleKey;
  label: string;
  hint: string;
}

/** Where the volumes start for a player who has never touched them. */
export const DEFAULT_VOLUME = 0.7;
export const DEFAULT_MUSIC_VOLUME = 0.6;

/**
 * What a player who has never opened this menu gets.
 *
 * **One object, because there were two.** The store seeded its own defaults from
 * `renderFeatures` and the title screen's options panel hard-coded a second set beside it,
 * so the two could disagree about what a fresh install looks like — and did, the moment one
 * of them changed.
 *
 * They answer different questions and both are needed: `renderFeatures` is *can this
 * environment do it* (a driver, a `?fog=0` in the URL, safe mode), and this is *do we want
 * it*. The store takes the conjunction; a capability that is absent wins over a preference
 * that is on, never the other way round.
 *
 * **`pixelate` is off** (owner's call, 2026-08-11: *the pixelate base should be removed by
 * default, I don't think it's nice — we can keep the option though*). The post-process, the
 * shader and the whole `MIN_OUTLINE_WIDTH` arithmetic that depends on it all stay: this is a
 * default, not a removal, and the switch is still in the options menu.
 */
export const DEFAULT_SETTINGS: Settings = {
  pixelate: false,
  // On: it is the look the idea asked for and the owner asked to have implemented (2026-09-27).
  grading: true,
  fog: true,
  shadows: true,
  volume: DEFAULT_VOLUME,
  musicVolume: DEFAULT_MUSIC_VOLUME,
};

/** One slider's identity and how to describe it. */
export interface SliderDef {
  key: SliderKey;
  label: string;
  hint: string;
}

/** Every slider, in the order the menu lists them. */
export const SLIDERS: readonly SliderDef[] = [
  {
    key: 'volume',
    label: 'Master Volume',
    hint: 'Everything the game plays — combat, drops and the interface.',
  },
  {
    key: 'musicVolume',
    label: 'Music',
    hint: 'The score, under the master. Three tracks: title, sanctuary, and the grind.',
  },
];

/** Every setting, in the order the menu lists them. */
export const SETTINGS: readonly SettingDef[] = [
  {
    key: 'pixelate',
    label: 'Pixel Art',
    hint: 'Chunky texels and a quantised palette over the viewport.',
  },
  {
    key: 'grading',
    label: 'Colour Grade',
    hint: 'Deeper blacks, a touch less colour in the dark, and darkened screen corners.',
  },
  {
    key: 'fog',
    label: 'Distance Fog',
    hint: 'The dark that swallows the far edge of a zone.',
  },
  {
    key: 'shadows',
    label: 'Shadows',
    hint: 'Cast shadows. The first thing to turn off if the frame rate suffers.',
  },
];

/** The storage the settings persist to. Injected so tests need no browser. */
export interface SettingsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORAGE_KEY = 'grind.settings';

/**
 * Reads saved settings, falling back to the supplied defaults.
 *
 * Deliberately forgiving: a corrupt or half-written value should cost the player
 * that one setting, not lock them out of the game behind a parse error.
 */
export function loadSettings(storage: SettingsStore | null, fallback: Settings): Settings {
  if (!storage) return { ...fallback };

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { ...fallback };

    const parsed = JSON.parse(raw) as Partial<Record<keyof Settings, unknown>>;
    return {
      pixelate: typeof parsed.pixelate === 'boolean' ? parsed.pixelate : fallback.pixelate,
      grading: typeof parsed.grading === 'boolean' ? parsed.grading : fallback.grading,
      fog: typeof parsed.fog === 'boolean' ? parsed.fog : fallback.fog,
      shadows: typeof parsed.shadows === 'boolean' ? parsed.shadows : fallback.shadows,
      // Clamped rather than trusted: a hand-edited 11 would be eleven times full
      // scale into the limiter, and a NaN would silence the game with no clue why.
      volume: clampVolume(parsed.volume, fallback.volume),
      musicVolume: clampVolume(parsed.musicVolume, fallback.musicVolume),
    };
  } catch {
    return { ...fallback };
  }
}

/** Persists settings, ignoring a storage that refuses to write. */
export function saveSettings(storage: SettingsStore | null, settings: Settings): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Private browsing, a full quota, a disabled store — none of which is worth
    // interrupting the game for.
  }
}

/** Keeps a stored volume inside 0..1, falling back on anything unusable. */
function clampVolume(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(1, value));
}

/** The browser's localStorage, or null where there is none. */
export function browserStorage(): SettingsStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
